import { formatTime, getHours } from "@clipboard-health/date-time";
import { getHours as getLocalHours, set as setDateValues } from "date-fns";

import { type TimeSlot } from "../types";

interface TimePart {
  hour: number;
  minute: number;
  second: number;
}

/**
 * Defines the allowable start time range for each time slot.
 * These ranges are intentionally OVERLAPPING to allow flexibility when validating
 * user-selected time slots. A shift can be valid in multiple slots.
 *
 * Used by: startTimeMismatchesTimeSlot (validation)
 */
export const TIME_SLOT_ALLOWABLE_START_TIME_RANGE: Record<
  TimeSlot,
  { start: TimePart; end: TimePart }
> = {
  am: {
    start: { hour: 0, minute: 0, second: 0 },
    end: { hour: 11, minute: 59, second: 59 },
  },
  pm: {
    start: { hour: 11, minute: 0, second: 0 },
    end: { hour: 22, minute: 59, second: 59 },
  },
  noc: {
    start: { hour: 17, minute: 0, second: 0 },
    end: { hour: 5, minute: 59, second: 59 },
  },
};

/**
 * Defines the recommended start time range for each time slot.
 * These ranges are CONTIGUOUS and NON-OVERLAPPING for unambiguous classification.
 *
 * Used by: getRecommendedTimeSlot (classification)
 *
 * Boundaries:
 * - AM:  00:00 - 10:59 (midnight to before 11am)
 * - PM:  11:00 - 16:59 (11am to before 5pm)
 * - NOC: 17:00 - 23:59 (5pm to midnight)
 */
export const TIME_SLOT_RECOMMENDED_START_TIME_RANGE: Record<
  TimeSlot,
  { start: TimePart; end: TimePart }
> = {
  am: {
    start: { hour: 0, minute: 0, second: 0 },
    end: { hour: 10, minute: 59, second: 59 },
  },
  pm: {
    start: { hour: 11, minute: 0, second: 0 },
    end: { hour: 16, minute: 59, second: 59 },
  },
  noc: {
    start: { hour: 17, minute: 0, second: 0 },
    end: { hour: 23, minute: 59, second: 59 },
  },
};

function getDateAtTime(date: Date, time: TimePart): Date {
  return setDateValues(date, { hours: time.hour, minutes: time.minute, seconds: time.second });
}

/**
 * Checks if the start time is outside the allowable range for the given time slot.
 *
 * @param timeSlot - The time slot (am, pm, noc)
 * @param startTime - The shift start time to validate (in UTC)
 * @param timezone - The workplace timezone to convert to before checking
 * @returns true if the start time does NOT match the time slot, false if it's valid
 */
export function startTimeMismatchesTimeSlot(
  timeSlot: TimeSlot,
  startTime: Date,
  timezone?: string
): boolean {
  const timeSlotRange = TIME_SLOT_ALLOWABLE_START_TIME_RANGE[timeSlot];

  // Get hour in workplace timezone if provided, otherwise local time
  const hour = timezone ? getHours(startTime, { timeZone: timezone }) : getLocalHours(startTime);

  // NOC shifts are calculated differently since they end (on the next day)
  // at an earlier hour than they start
  switch (timeSlot) {
    case "am":
    case "pm": {
      return hour < timeSlotRange.start.hour || hour > timeSlotRange.end.hour;
    }
    case "noc": {
      return hour < timeSlotRange.start.hour && hour > timeSlotRange.end.hour;
    }
    default: {
      return false;
    }
  }
}

/**
 * Gets the recommended time slot based on the start time.
 * This is used to suggest which time slot the shift should be in.
 *
 * Uses TIME_SLOT_RECOMMENDED_START_TIME_RANGE for unambiguous classification:
 * - AM:  00:00 - 10:59 (midnight to before 11am)
 * - PM:  11:00 - 16:59 (11am to before 5pm)
 * - NOC: 17:00 - 23:59 (5pm to midnight)
 *
 * @param startTime - The shift start time (in UTC)
 * @param timezone - The workplace timezone to convert to before checking
 * @returns The recommended time slot
 */
export function getRecommendedTimeSlot(startTime: Date, timezone?: string): TimeSlot {
  // Get hour in workplace timezone if provided, otherwise local time
  const hour = timezone ? getHours(startTime, { timeZone: timezone }) : getLocalHours(startTime);

  const { am, pm } = TIME_SLOT_RECOMMENDED_START_TIME_RANGE;

  // AM: 00:00 - 10:59
  if (hour >= am.start.hour && hour <= am.end.hour) {
    return "am";
  }

  // PM: 11:00 - 16:59
  if (hour >= pm.start.hour && hour <= pm.end.hour) {
    return "pm";
  }

  // NOC: 17:00 - 23:59
  return "noc";
}

/**
 * Generates a warning message when the start time doesn't match the time slot.
 *
 * @param timeSlot - The time slot (am, pm, noc)
 * @returns A warning message explaining the expected time range
 */
export function getTimeSlotMismatchWarning(timeSlot: TimeSlot): string {
  const range = TIME_SLOT_ALLOWABLE_START_TIME_RANGE[timeSlot];
  const formattedStartTime = formatTime(getDateAtTime(new Date(), range.start));
  const formattedEndTime = formatTime(getDateAtTime(new Date(), range.end));
  const upperCaseTimeSlot = timeSlot.toUpperCase();

  return `${upperCaseTimeSlot} shifts should have a start time between ${formattedStartTime} - ${formattedEndTime}. The shift will be reclassified based on its start time.`;
}
